import React from "react";
import { nanoid } from "nanoid/non-secure";

import { MediaSource } from "./ImageViewing/@types";
import { useNonReactiveCallback } from "../useNonReactiveCallback";

export type Lightbox = {
  images: MediaSource[];
  index: number;
};

export type LightboxInternal = {
  id: string;
  images: (MediaSource & { id: string })[];
  index: number;
};

const LightboxContext = React.createContext<{
  activeLightbox: LightboxInternal | null;
}>({
  activeLightbox: null,
});

const LightboxControlContext = React.createContext<{
  openLightbox: (lightbox: Omit<Lightbox, "id">) => void;
  closeLightbox: () => boolean;
}>({
  openLightbox: () => {},
  closeLightbox: () => false,
});

export function LightboxProvider({ children }: React.PropsWithChildren) {
  const [activeLightbox, setActiveLightbox] =
    React.useState<LightboxInternal | null>(null);

  const openLightbox = useNonReactiveCallback(
    (lightbox: Omit<Lightbox, "id">) => {
      setActiveLightbox((prevLightbox) => {
        if (prevLightbox) {
          // Ignore duplicate open requests. If it's already open,
          // the user has to explicitly close the previous one first.
          return prevLightbox;
        } else {
          return {
            ...lightbox,
            id: nanoid(),
            images: lightbox.images.map((image) => ({
              ...image,
              id: nanoid(),
            })),
          };
        }
      });
    }
  );

  const closeLightbox = useNonReactiveCallback(() => {
    let wasActive = !!activeLightbox;
    setActiveLightbox(null);
    return wasActive;
  });

  const state = React.useMemo(
    () => ({
      activeLightbox,
    }),
    [activeLightbox]
  );

  const methods = React.useMemo(
    () => ({
      openLightbox,
      closeLightbox,
    }),
    [openLightbox, closeLightbox]
  );

  return (
    <LightboxContext.Provider value={state}>
      <LightboxControlContext.Provider value={methods}>
        {children}
      </LightboxControlContext.Provider>
    </LightboxContext.Provider>
  );
}

export function useLightbox() {
  return React.useContext(LightboxContext);
}

export function useLightboxControls() {
  return React.useContext(LightboxControlContext);
}
